// cli/src/commands/integration.ts
import path from "node:path";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import * as claudeCode from "./plugin/claude-code.ts";
import * as codex from "./plugin/codex.ts";
import * as omp from "./plugin/omp.ts";
import type { BackendName, BackendStatus } from "./plugin/types.ts";

const ALL_BACKENDS: readonly BackendName[] = ["claude-code", "omp", "codex"];
const TOGGLE_SUPPORTED: readonly BackendName[] = ["claude-code"];

function resolveDriver(driver: string): BackendName[] {
  if (driver === "all") return [...ALL_BACKENDS];
  if (driver === "claude-code" || driver === "omp" || driver === "codex") return [driver];
  throw new Error(`unknown driver: ${driver} (expected: claude-code | omp | codex | all)`);
}

interface RunOptions {
  driver: string;
  projectDir: string;
  /** Claude Code only — generate the full `.claude/plugins/skaile/` directory. */
  full?: boolean;
}

/** Run one backend's install/uninstall body, reporting failures as exit-code 1. */
async function runBackendStep(label: string, body: () => Promise<void>): Promise<number> {
  try {
    await body();
    return 0;
  } catch (e: unknown) {
    logErr(`${label}: ${e instanceof Error ? e.message : String(e)}`);
    return 1;
  }
}

async function installClaudeCode(projectDir: string, opts: RunOptions): Promise<void> {
  const r = await claudeCode.install(projectDir, { full: opts.full });
  if (r.warning) logWarn(r.warning);
  if (r.method === "plugin") {
    logOk(
      `Generated ${S.cmd(".claude/plugins/skaile/")} and registered it in ${S.cmd(".claude/settings.json")}`,
    );
  } else if (r.changed) {
    logOk(`Wrote ${S.cmd(".claude/mcp.json")} (${SKAILE_MCP_LABEL})`);
  } else {
    logInfo(`claude-code: already installed (mcp)`);
  }
  console.log(`  ${S.dim("→")} Restart Claude Code to pick up the change.`);
}

async function installOmp(projectDir: string): Promise<void> {
  const r = await omp.install(projectDir);
  if (r.changed) {
    logOk(`Wrote ${S.cmd(".omp/extensions/skaile.ts")} (${r.method})`);
  } else {
    logInfo(`omp: already installed (${r.method})`);
  }
  console.log(`  ${S.dim("→")} OMP auto-discovers extensions on next startup.`);
}

async function installCodex(projectDir: string): Promise<void> {
  const r = await codex.install(projectDir);
  if (r.warning) logWarn(r.warning);
  if (r.changed) {
    logOk(`Wrote ${S.cmd(".codex/config.toml")} (${SKAILE_MCP_LABEL})`);
  } else {
    logInfo(`codex: already installed`);
  }
  console.log(`  ${S.dim("→")} Restart Codex CLI to pick up the change.`);
}

async function runInstall(opts: RunOptions): Promise<number> {
  const projectDir = path.resolve(opts.projectDir);
  const targets = resolveDriver(opts.driver);
  let exitCode = 0;

  if (targets.includes("claude-code")) {
    exitCode |= await runBackendStep("claude-code install", () =>
      installClaudeCode(projectDir, opts),
    );
  }
  if (targets.includes("omp")) {
    exitCode |= await runBackendStep("omp install", () => installOmp(projectDir));
  }
  if (targets.includes("codex")) {
    exitCode |= await runBackendStep("codex install", () => installCodex(projectDir));
  }

  if (exitCode === 0) logOk("Plugin installed.");
  return exitCode;
}

async function uninstallClaudeCode(projectDir: string): Promise<void> {
  const r = await claudeCode.uninstall(projectDir);
  if (r.changed) logOk(`Removed skaile entries from ${S.cmd(".claude/")}`);
  else logInfo(`claude-code: not installed`);
}

async function uninstallOmp(projectDir: string): Promise<void> {
  const r = await omp.uninstall(projectDir);
  if (r.changed) logOk(`Removed ${S.cmd(".omp/extensions/skaile.ts")}`);
  else logInfo(`omp: not installed`);
}

async function uninstallCodex(projectDir: string): Promise<void> {
  const r = await codex.uninstall(projectDir);
  if (r.changed) logOk(`Removed skaile entry from ${S.cmd(".codex/config.toml")}`);
  else logInfo(`codex: not installed`);
}

async function runUninstall(opts: RunOptions): Promise<number> {
  const projectDir = path.resolve(opts.projectDir);
  const targets = resolveDriver(opts.driver);
  let exitCode = 0;

  if (targets.includes("claude-code")) {
    exitCode |= await runBackendStep("claude-code uninstall", () =>
      uninstallClaudeCode(projectDir),
    );
  }
  if (targets.includes("omp")) {
    exitCode |= await runBackendStep("omp uninstall", () => uninstallOmp(projectDir));
  }
  if (targets.includes("codex")) {
    exitCode |= await runBackendStep("codex uninstall", () => uninstallCodex(projectDir));
  }

  if (exitCode === 0) logOk("Plugin uninstalled.");
  return exitCode;
}

/**
 * Reject explicit unsupported drivers with a usage error. Returns `2` when the
 * caller should bail with that exit code, or `null` to proceed.
 */
function checkToggleSupported(
  targets: BackendName[],
  requestedAll: boolean,
  action: "enable" | "disable",
): number | null {
  if (requestedAll) return null;
  const unsupported = targets.filter((t) => !TOGGLE_SUPPORTED.includes(t));
  if (unsupported.length === 0) return null;
  logErr(
    `${action} is only supported for claude-code; for ${unsupported.join(", ")} use uninstall/install`,
  );
  return 2;
}

/** Toggle a single backend. Returns its exit-code contribution (0 or 1). */
async function toggleTarget(
  t: BackendName,
  projectDir: string,
  action: "enable" | "disable",
): Promise<number> {
  if (!TOGGLE_SUPPORTED.includes(t)) {
    logInfo(`${t}: ${action} not supported; skipping`);
    return 0;
  }
  if (t !== "claude-code") return 0;
  return runBackendStep(`claude-code ${action}`, async () => {
    const r =
      action === "enable"
        ? await claudeCode.enable(projectDir)
        : await claudeCode.disable(projectDir);
    if (r.changed) {
      logOk(`claude-code: ${action}d`);
    } else {
      logInfo(`claude-code: already ${action}d`);
    }
  });
}

async function runToggle(opts: RunOptions, action: "enable" | "disable"): Promise<number> {
  const projectDir = path.resolve(opts.projectDir);
  const requestedAll = opts.driver === "all";
  const targets = resolveDriver(opts.driver);

  const usageError = checkToggleSupported(targets, requestedAll, action);
  if (usageError !== null) return usageError;

  let exitCode = 0;
  for (const t of targets) {
    exitCode |= await toggleTarget(t, projectDir, action);
  }
  return exitCode;
}

async function runStatus(opts: RunOptions): Promise<number> {
  const projectDir = path.resolve(opts.projectDir);
  const targets = resolveDriver(opts.driver);
  const rows: BackendStatus[] = [];
  let exitCode = 0;

  for (const t of targets) {
    try {
      if (t === "claude-code") rows.push(await claudeCode.status(projectDir));
      else if (t === "omp") rows.push(await omp.status(projectDir));
      else rows.push(await codex.status(projectDir));
    } catch (e: unknown) {
      logErr(`${t} status: ${e instanceof Error ? e.message : String(e)}`);
      exitCode = 1;
    }
  }

  printStatusTable(rows);
  return exitCode;
}

function printStatusTable(rows: BackendStatus[]): void {
  if (rows.length === 0) return;

  const cols: Array<{ key: keyof BackendStatus; header: string }> = [
    { key: "backend", header: "backend" },
    { key: "installed", header: "installed" },
    { key: "enabled", header: "enabled" },
    { key: "method", header: "method" },
    { key: "location", header: "location" },
  ];

  const widths = cols.map((c) =>
    Math.max(c.header.length, ...rows.map((r) => String(r[c.key] ?? "").length)),
  );

  const fmtRow = (parts: string[]): string =>
    parts.map((p, i) => p.padEnd(widths[i] ?? p.length)).join("  ");

  console.log();
  console.log(`  ${S.heading(fmtRow(cols.map((c) => c.header)))}`);
  for (const r of rows) {
    console.log(`  ${fmtRow(cols.map((c) => String(r[c.key] ?? "")))}`);
    if (r.detail) console.log(`    ${S.dim(r.detail)}`);
  }
  console.log();
}

const SKAILE_MCP_LABEL = "skaile-workspace entry";

// ── Claude Code lifecycle hooks ─────────────────────────────────────────────

const HOOK_EVENTS = ["session-start", "session-end", "user-prompt-submit"] as const;
type HookEvent = (typeof HOOK_EVENTS)[number];

/**
 * Run a Claude Code lifecycle hook.
 *
 * Replaces the three former loose `.mjs` hook scripts. The Claude Code plugin
 * generated by `buildClaudePluginFiles()` wires each lifecycle event to
 * `skaile plugin hook <event>`.
 *
 * Structured logging is routed to stderr so the JSON result this hook writes
 * to stdout stays uncorrupted.
 *
 * @param event - The lifecycle event to run.
 */
async function runHook(event: HookEvent): Promise<void> {
  // stdout carries the hook's JSON result — route all logging to stderr.
  const { LogStore, StdoutSink, registerLogStore } = await import(
    "@skaile/workspaces/core/logging"
  );
  registerLogStore(
    new LogStore({
      sessionId: `hook-${event}`,
      level: "info",
      sinks: [new StdoutSink({ stream: "stderr" })],
    }),
  );

  const { WorkspacePlugin, PluginStore, resolveProjectDir } = await import(
    "@skaile/workspaces/workspace-plugin"
  );
  const projectDir = resolveProjectDir(["CLAUDE_PROJECT_DIR"]);

  if (event === "session-start") {
    const plugin = new WorkspacePlugin({ projectDir, bootOwned: true });
    await plugin.boot();
    const additionalContext = plugin.buildSystemPromptSection();
    process.stdout.write(
      `${JSON.stringify({
        hookSpecificOutput: { hookEventName: "SessionStart", additionalContext },
      })}\n`,
    );
    await plugin.shutdown();
    return;
  }

  if (event === "session-end") {
    const plugin = new WorkspacePlugin({ projectDir, bootOwned: true });
    await plugin.boot();
    await plugin.store.flush();
    await plugin.shutdown();
    return;
  }

  // user-prompt-submit — lightweight: read PluginStore from disk, no full boot.
  const store = new PluginStore(path.join(projectDir, ".skaile", "plugin-state.json"));
  await store.load();
  const status = store.get("connector:status");
  if (status && typeof status === "object" && Object.keys(status as object).length > 0) {
    process.stdout.write(
      `${JSON.stringify({
        hookSpecificOutput: {
          hookEventName: "UserPromptSubmit",
          additionalContext: `Connector status update: ${JSON.stringify(status)}`,
        },
      })}\n`,
    );
  }
}

// ── CLI command ────────────────────────────────────────────────────────────

/**
 * Creates the `skaile integration` command group (agent integration /
 * workspace-plugin adapter).
 *
 * Manages the skaile workspace integration across coding agent backends
 * (Claude Code, OMP, Codex). Subcommands: `install`, `uninstall`, `enable`,
 * `disable`, `status`. All subcommands accept `--driver` to target a specific
 * backend or `all`.
 *
 * Renamed from `skaile plugin` — that name now belongs to the registry-plugin
 * commands (`@skaile/workspaces/plugin-registry`). The subcommand behaviour
 * here is unchanged.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/integration#make-integration-command
 */
export function makeIntegrationCommand(): Command {
  const cmd = new Command("integration").description(
    "Manage skaile agent integration / workspace-plugin adapter (Claude Code / OMP / Codex)",
  );

  cmd
    .command("install")
    .description("Install skaile workspace plugin")
    .option("--driver <name>", "Target: claude-code | omp | codex | all", "all")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .option(
      "--full",
      "Claude Code: generate the full .claude/plugins/skaile/ directory (MCP + hooks) instead of the default MCP-only install",
    )
    .action(async (opts: RunOptions) => {
      process.exitCode = await runInstall(opts);
    });

  cmd
    .command("uninstall")
    .description("Uninstall skaile workspace plugin")
    .option("--driver <name>", "Target: claude-code | omp | codex | all", "all")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts: RunOptions) => {
      process.exitCode = await runUninstall(opts);
    });

  cmd
    .command("enable")
    .description("Enable an installed plugin (claude-code only)")
    .option("--driver <name>", "Target: claude-code | all", "claude-code")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts: RunOptions) => {
      process.exitCode = await runToggle(opts, "enable");
    });

  cmd
    .command("disable")
    .description("Disable an installed plugin (claude-code only)")
    .option("--driver <name>", "Target: claude-code | all", "claude-code")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts: RunOptions) => {
      process.exitCode = await runToggle(opts, "disable");
    });

  cmd
    .command("status")
    .description("Show install / enable status across backends")
    .option("--driver <name>", "Target: claude-code | omp | codex | all", "all")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts: RunOptions) => {
      process.exitCode = await runStatus(opts);
    });

  cmd
    .command("hook <event>")
    .description(
      "Run a Claude Code lifecycle hook (session-start | session-end | user-prompt-submit)",
    )
    .action(async (event: string) => {
      try {
        if (!HOOK_EVENTS.includes(event as HookEvent)) {
          process.stderr.write(
            `plugin hook: unknown event '${event}' (expected: ${HOOK_EVENTS.join(" | ")})\n`,
          );
        } else {
          await runHook(event as HookEvent);
        }
      } catch (err) {
        // Never block the agent — hooks always exit 0.
        process.stderr.write(
          `plugin hook ${event} warning: ${err instanceof Error ? err.message : String(err)}\n`,
        );
      }
      process.exit(0);
    });

  cmd.action(() => cmd.help());
  return cmd;
}
